import { getDomain } from '@renderer/chat/components/chat/attachments/favicon'

// Helper function to extract domain from email
export function getDomainFromEmail(email: string): string | undefined {
  const parts = email.split('@')
  if (parts.length === 2) {
    return parts[1].toLowerCase()
  }
  // Fall back to getDomain for URLs or other formats
  return getDomain(email)
}

// Common email providers that should be grouped separately
export const COMMON_PROVIDERS = new Set([
  'gmail.com',
  'outlook.com',
  'hotmail.com',
  'yahoo.com',
  'icloud.com',
  'me.com',
  'aol.com',
  'protonmail.com',
  'proton.me',
  'live.com',
  'msn.com',
  'mail.com',
])

// Format domain name for display
export function formatDomainName(domain: string): string {
  // Special handling for internal groups
  if (domain === '_personal') return 'Personal'
  if (domain === '_other') return 'Other'

  // Extract the domain name before the TLD (everything before the last dot)
  const lastDotIndex = domain.lastIndexOf('.')
  if (lastDotIndex === -1) return domain // No TLD found, return as-is

  const nameWithoutTLD = domain.substring(0, lastDotIndex)

  // Capitalize first letter of each word (for domains like "highlight-ai")
  // Split on hyphens but not dots
  return nameWithoutTLD
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// Group emails by domain with special handling for common providers
export function groupEmailsByDomain(emails: string[]): Record<string, string[]> {
  const groups: Record<string, string[]> = {}

  for (const email of emails || []) {
    const domain = getDomainFromEmail(email)

    if (!domain) {
      // Handle participants without domains (e.g., names)
      if (!groups['_other']) groups['_other'] = []
      groups['_other'].push(email)
      continue
    }

    // Check if it's a common provider
    if (COMMON_PROVIDERS.has(domain)) {
      if (!groups['_personal']) groups['_personal'] = []
      groups['_personal'].push(email)
    } else {
      if (!groups[domain]) groups[domain] = []
      groups[domain].push(email)
    }
  }

  return groups
}

// Sort domains with special groups at the end
export function sortDomains(domains: string[]): string[] {
  const regularDomains = domains.filter((d) => !d.startsWith('_')).sort((a, b) => a.localeCompare(b))
  const hasPersonal = domains.includes('_personal')
  const hasOther = domains.includes('_other')

  const result = [...regularDomains]
  if (hasPersonal) result.push('_personal')
  if (hasOther) result.push('_other')

  return result
}
